import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import { BookingBasket } from '../components/BookingBasket';
import { SubscriptionPlanOffcanvas } from '../components/SubscriptionPlanOffcanvas';
import { CmsModal } from '../components/CmsModal';
import { ConfirmModal } from '../components/ConfirmModal';
import ExpectationPhotos from '../components/ExpectationPhotos';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import {
  createOneOffCheckout,
  getProAvailability,
  listAddons,
  listCategories,
  listServices,
  type AvailabilitySlot,
  type ServiceAddonRow,
  type ServiceCategory,
  type ServiceItem,
} from '../../lib/booking-api';
import {
  customerApiErrorMessage,
  getMyWallet,
  startMembershipCheckout,
} from '../../lib/customer-api';
import {
  clearBookingDraft,
  patchBookingDraft,
  useBookingDraft,
} from '../../lib/booking-draft';
import { RequireCustomer } from '../../lib/use-customer-auth';
import { trackInitiateCheckout } from '../../lib/analytics';
import {
  ukSlotToISO,
  isoToUkHm,
  isoToUkParts,
  isoToUkDateOnly,
} from '../../lib/uk-time';
import { toast } from '../../lib/toast';

const formatMoney = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

// How far ahead we fetch (and therefore allow) bookings. The customer
// can only select dates the server has confirmed availability for, so
// this doubles as the booking horizon. Kept under the backend's 62-day
// availability range cap.
const AVAILABILITY_WINDOW_DAYS = 60;

const ymd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Interpret the slot as UK (Europe/London) wall-clock regardless of the
// browser's timezone — see lib/uk-time. Browser-local setHours() shifted
// bookings from non-UK browsers and the checkout then rejected the slot.
const combineDateAndTime = (date: Date, hhmm: string): string =>
  ukSlotToISO(date, hhmm);


const composeAddress = (a?: {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  postcode?: string | null;
} | null): string => {
  if (!a) return '';
  return [a.address_line1, a.address_line2, a.city, a.postcode]
    .filter((x): x is string => !!x && x.trim() !== '')
    .join(', ');
};

export default function YourAppointmentPage() {
  return (
    <RequireCustomer>
      <YourAppointmentPageInner />
    </RequireCustomer>
  );
}

function YourAppointmentPageInner() {
  const navigate = useNavigate();
  const draft = useBookingDraft();

  // Wizard step. 1: pick date, 2: pick time, 3: confirm + pay.
  // Skip straight to step 3 if the customer already picked a date +
  // time in the /booking wizard — no point asking them twice.
  const [step, setStep] = useState<number>(() => {
    const draftStart = draft?.scheduled_start
      ? new Date(draft.scheduled_start)
      : null;
    if (draftStart && !Number.isNaN(draftStart.getTime())) {
      // Read the saved time as UK wall-clock (NOT browser-local) so a non-UK
      // browser doesn't misjudge "is a real time set?" and land on the wrong
      // step.
      const { hh, mm } = isoToUkParts(draft!.scheduled_start as string);
      // A real time was set (not just a midnight default) → jump to
      // the review/pay screen.
      if (hh !== 0 || mm !== 0) return 3;
      return 2;
    }
    return 1;
  });

  // Date defaults to whatever the customer picked on SelectProfessionalPage
  // (draft.scheduled_start), otherwise +7 days.
  const [date, setDate] = useState<Date>(() => {
    if (draft?.scheduled_start) {
      const d = new Date(draft.scheduled_start);
      // Use the UK calendar day (browser-local getDate() can be the wrong
      // day near midnight on a non-UK browser).
      if (!Number.isNaN(d.getTime())) return isoToUkDateOnly(draft.scheduled_start);
    }
    const fallback = new Date();
    fallback.setDate(fallback.getDate() + 7);
    return fallback;
  });
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Time slot.
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string>(() => {
    if (draft?.scheduled_start) {
      try {
        // UK wall-clock "HH:MM" so it matches the UK availability slot list on
        // ANY browser. Browser-local getHours() shifted this on non-UK zones,
        // so the saved slot never matched and was dropped at checkout.
        return isoToUkHm(draft.scheduled_start);
      } catch {
        return '';
      }
    }
    return '';
  });

  // Catalogue (so we can show service name + price in the basket).
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [addonsCatalog, setAddonsCatalog] = useState<ServiceAddonRow[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  // Pricing rate-lock token + last-known total from the basket's
  // most recent /quote. We echo `pricingVersion` back on
  // createOneOffCheckout — if platform-wide pricing (VAT / platform
  // fee / active charges) drifted between basket render and Book-
  // and-Pay click, the backend 409s with a fresh quote so we can
  // show "Pricing has been updated — review + confirm new total"
  // before burning wallet / hitting Stripe. `serverQuoteTotal` is
  // the customer-facing total at quote time; we surface it in the
  // 409 modal as the "old" total alongside the new one.
  const [pricingVersion, setPricingVersion] = useState<string | null>(null);
  const [serverQuoteTotal, setServerQuoteTotal] = useState<number | null>(null);
  // Meta Pixel `InitiateCheckout` — fired once, when the review/pay step (3)
  // is reached AND the server quote total is known, so the reported `value`
  // is the real customer-facing total (not a stale local estimate).
  const initiateCheckoutFired = useRef(false);
  useEffect(() => {
    if (step !== 3 || initiateCheckoutFired.current) return;
    if (serverQuoteTotal == null) return; // wait for the quote's total
    initiateCheckoutFired.current = true;
    const ids = new Set<string>();
    if (draft?.service_id) ids.add(draft.service_id);
    for (const it of (draft?.items ?? []) as Array<{
      service_id?: string | null;
    }>) {
      if (it?.service_id) ids.add(it.service_id);
    }
    trackInitiateCheckout({
      value: serverQuoteTotal,
      currency: 'GBP',
      contentIds: [...ids],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, serverQuoteTotal]);
  // Set when a 409 PRICING_CHANGED comes back. Renders a ConfirmModal
  // showing the new total + the new pricing_version. On confirm we
  // retry the checkout with the latest token; on cancel the customer
  // stays put and the basket has already re-rendered with new numbers.
  const [pendingPriceChange, setPendingPriceChange] = useState<{
    oldTotal: number;
    newTotal: number;
    newVersion: string;
    currency: string;
  } | null>(null);

  // Wallet integration — fetched on mount so we know how much the
  // customer can apply against this booking. Opt-in via a checkbox so
  // they don't drain the wallet on a small booking accidentally, and
  // the amount is editable so the customer can save some balance for
  // later by only applying part of it.
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletCurrency, setWalletCurrency] = useState('GBP');
  const [useWallet, setUseWallet] = useState<boolean>(
    () => Number(draft?.wallet_amount ?? 0) > 0,
  );
  const [walletAmountToApply, setWalletAmountToApply] = useState<number>(
    () => Number(draft?.wallet_amount ?? 0),
  );

  // Subscription upsell — mirrors the /booking page so the basket
  // component behaves the same here. Guest gate is unnecessary on this
  // page (RequireCustomer wraps it) but we keep the flow identical.
  const [isSubscriptionPlanOpen, setSubscriptionPlanOpen] = useState(false);
  // Inline CMS modals so the customer doesn't lose their place by
  // following a link to /cancellation-policy or /terms-of-use mid-
  // checkout. Same pattern as the sign-up form's T&C link.
  const [policyOpen, setPolicyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const handleSubscribeClick = () => setSubscriptionPlanOpen(true);
  const handlePickPlan = async (planId: string) => {
    // Close the offcanvas first so any error toast is visible.
    setSubscriptionPlanOpen(false);
    try {
      const res = await startMembershipCheckout({
        subscription_id: planId,
        subscription_type: 'MONTHLY',
      });
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        toast.error('Could not start checkout. Please try again.');
      }
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not start checkout. Please try again.'),
      );
    }
  };

  // Pre-flight: customer must have arrived here with the basics
  // captured in earlier wizard steps. Surface a friendly error rather
  // than letting the basket render with nulls / sending an invalid
  // payload to Stripe.
  //
  // A professional is required ONLY for the TREATMENT flow (hair / beauty):
  // that booking is tied to a specific pro, and this page's time picker is
  // driven by THAT pro's availability (getProAvailability below).
  //
  // SESSION bookings (cleaning / childcare) have no hand-picked pro — the
  // wizard already captured the date/time and the platform assigns a pro after
  // checkout. Demanding one here dead-ended every cleaning customer: they
  // reach this page for the FIRST time straight after creating an account
  // (BookingFlowPage sends unauthenticated customers to sign-up with
  // `from: '/your-appointment'`), so it read as "signing up broke my booking".
  //
  // Safe to read synchronously: both entry points stamp `flow_entry` BEFORE
  // navigating here — PublicProfilePage → 'TREATMENT', BookingFlowPage →
  // 'SESSION'. (The `isTreatmentFlow` memo below can't be used: it depends on
  // the async-loaded category catalog.)
  const requiresProfessional = draft?.meta?.flow_entry === 'TREATMENT';

  const missing: string[] = [];
  if (!draft?.service_id) missing.push('service');
  if (requiresProfessional && !draft?.professional_id)
    missing.push('professional');
  if (!draft?.address?.address_line1) missing.push('address');
  // Only the pro is missing → recoverable in one click; don't make them redo
  // the service + address they already entered.
  const onlyProMissing =
    missing.length === 1 && missing[0] === 'professional';

  // Human-readable phrasing for the pre-flight copy: "a service and an
  // address" rather than the bare "service, address" (which produced the
  // ungrammatical "We need professional").
  const MISSING_LABELS: Record<string, string> = {
    service: 'a service',
    professional: 'a professional',
    address: 'an address',
  };
  const missingPhrase = (() => {
    const parts = missing.map((m) => MISSING_LABELS[m] ?? m);
    if (parts.length <= 1) return parts[0] ?? '';
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  })();

  useEffect(() => {
    (async () => {
      try {
        const [svcRes, catRes] = await Promise.all([
          listServices(),
          listCategories(),
        ]);
        setServices(svcRes.data ?? []);
        setCategories(catRes.data ?? []);
      } catch {
        // Non-blocking — basket falls back to placeholder copy.
      } finally {
        setCatalogLoading(false);
      }
    })();
  }, []);

  // Load add-ons for the booking's category so the basket can resolve
  // ids in draft.meta.addons to names + prices (same source the /booking
  // page reads).
  useEffect(() => {
    const categoryId = draft?.service_category_id;
    if (!categoryId) return;
    (async () => {
      try {
        const res = await listAddons(categoryId);
        setAddonsCatalog(res.data ?? []);
      } catch {
        // Non-blocking — basket renders without add-on labels.
      }
    })();
  }, [draft?.service_category_id]);

  // Fetch wallet balance on mount AND whenever the tab regains
  // focus / becomes visible. The basket's walletAvailable label is
  // the customer's only signal of how much they have, and without
  // revalidation a long-open tab could show a stale £50 after the
  // customer spent £30 in another tab — even though
  // handleBookAndPay's pre-submit refetch catches the over-spend,
  // the customer's mental model is already wrong by then (audit
  // 2026-06-04, medium).
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await getMyWallet();
        if (cancelled) return;
        setWalletBalance(Number(res?.balance ?? 0));
        setWalletCurrency(res?.currency ?? 'GBP');
      } catch {
        /* non-blocking */
      }
    };
    void refresh();
    const onFocus = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // Cache the pro's availability across a 30-day window so the
  // calendar can grey out days with zero slots without a round-trip
  // per click. Refetched only when the pro / duration changes (date
  // pick uses the cached map).
  const [slotsByDate, setSlotsByDate] = useState<
    Record<string, AvailabilitySlot[]>
  >({});
  useEffect(() => {
    if (missing.length > 0) return;
    if (!draft?.professional_id) return;
    setSlotsLoading(true);
    setSlotsError(null);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Fetch the whole bookable horizon up front so the calendar can
    // validate every date the customer might navigate to. The customer
    // can only pick days the server confirms the pro has open slots on
    // (see `availableYmds` below), so the window length IS the booking
    // horizon — kept just under the backend's 62-day range cap.
    const end = new Date(
      today.getTime() + AVAILABILITY_WINDOW_DAYS * 24 * 60 * 60 * 1000,
    );
    getProAvailability({
      professional_id: draft.professional_id,
      date_from: ymd(today),
      date_to: ymd(end),
      duration_minutes: draft.duration_minutes ?? 60,
    })
      .then((res) => {
        const map = res.data?.slots_by_date ?? {};
        setSlotsByDate(map);
      })
      .catch((err) => {
        setSlotsError(
          customerApiErrorMessage(err, 'Could not load available times.'),
        );
        setSlotsByDate({});
      })
      .finally(() => setSlotsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.professional_id, draft?.duration_minutes]);

  // Drive the per-day time picker off the cached map. When the user
  // picks a new date in the calendar we just look up — no network hit.
  //
  // Why we DON'T clear the saved slot on the first render: the effect
  // fires once with `slotsByDate = {}` (the initial state, before the
  // availability fetch lands). Comparing against an empty list would
  // wipe the slot the customer just picked in the wizard, and the
  // subsequent Book-and-Pay bounces them back to step 2 with the
  // "Pick a time slot before continuing" toast — exactly the bug
  // the customer reported. We hold the selection until the fetch
  // genuinely reports the date as unavailable.
  //
  // Slot string normalisation: the draft persists scheduled_start as
  // an ISO datetime (init derives "HH:MM" from it); the API returns
  // `start` strings that may be "HH:MM" or "HH:MM:SS" depending on
  // the backend formatter. Comparing raw strings used to drop the
  // customer's previously-picked slot whenever the slot list reloaded.
  useEffect(() => {
    const list = slotsByDate[ymd(date)] ?? [];
    setSlots(list);
    if (!selectedSlot) return;
    // Fetch hasn't landed yet → leave the saved slot alone. We'll
    // re-evaluate once `slotsByDate` populates.
    if (slotsLoading) return;
    if (Object.keys(slotsByDate).length === 0) return;
    const toHm = (s: string) => (s || '').slice(0, 5);
    const stillAvailable = list.some(
      (s) => toHm(s.start) === toHm(selectedSlot),
    );
    if (!stillAvailable) setSelectedSlot('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, slotsByDate, slotsLoading]);

  // Set of YYYY-MM-DD strings the pro actually has open slots on. The
  // calendar enables ONLY these days and disables everything else —
  // non-working weekdays, holidays, fully-booked days, and any date
  // beyond the fetched window. Building the allow-list (rather than a
  // block-list of empty days) is what stops the customer selecting a
  // date the availability fetch never validated: previously any day
  // outside the fetched window wasn't in the map at all, so it slipped
  // through the "is this day empty?" check and stayed selectable even
  // when it was a holiday / non-working day for the pro.
  const availableYmds = useMemo(() => {
    const out = new Set<string>();
    for (const [ymdKey, list] of Object.entries(slotsByDate)) {
      if (list && list.length > 0) out.add(ymdKey);
    }
    return out;
  }, [slotsByDate]);

  // Soonest date in the next-30-day window with at least one slot.
  // Drives the "next available: …" hint when the chosen date is empty
  // and powers the fully-empty fallback.
  const nextAvailableDate = useMemo(() => {
    const dates = Object.entries(slotsByDate)
      .filter(([, list]) => list && list.length > 0)
      .map(([d]) => d)
      .sort();
    return dates[0] ?? null;
  }, [slotsByDate]);

  // True when the pro has zero slots across the whole 30-day window.
  // Triggers the "browse other pros" fallback UI.
  const fullyBooked = useMemo(() => {
    if (slotsLoading) return false;
    if (Object.keys(slotsByDate).length === 0) return false;
    return Object.values(slotsByDate).every((list) => !list || list.length === 0);
  }, [slotsByDate, slotsLoading]);

  const resolvedService = useMemo(() => {
    if (!draft?.service_id) return undefined;
    return services.find((s) => s.id === draft.service_id);
  }, [services, draft?.service_id]);

  // Patch-test requirement: the customer must tick a mandatory confirmation
  // at checkout before paying when the booking includes a flagged treatment.
  // Covers EVERY service in the booking — the primary draft.service_id PLUS
  // any additional draft.items the customer picked — so the tick box appears
  // if AT LEAST ONE requires a patch test (per spec), not just the primary.
  const needsPatchTest = useMemo(() => {
    const ids = new Set<string>();
    if (draft?.service_id) ids.add(draft.service_id);
    for (const it of (draft?.items ?? []) as Array<{
      service_id?: string | null;
    }>) {
      if (it?.service_id) ids.add(it.service_id);
    }
    return [...ids].some(
      (id) => !!services.find((s) => s.id === id)?.requires_patch_test,
    );
  }, [services, draft?.service_id, draft?.items]);
  const [patchTestAck, setPatchTestAck] = useState(false);

  // ---------- Basket props (mirrors /booking so totals match) ----------
  const basketServiceName = resolvedService?.name ?? 'Your service';
  const basketServicePrice = Number(resolvedService?.base_price ?? 0);
  const basketAddress = composeAddress(draft?.address ?? null);
  // Resolve hours from the draft. duration_minutes wins (set by the
  // wizard); meta.hours is a backup; otherwise the catalog default.
  const basketHours = useMemo(() => {
    const fromDraft = draft?.duration_minutes;
    if (typeof fromDraft === 'number' && fromDraft > 0) return fromDraft / 60;
    const fromMeta = draft?.meta?.hours;
    if (typeof fromMeta === 'number' && fromMeta > 0) return fromMeta;
    return (resolvedService?.duration_minutes ?? 60) / 60;
  }, [draft?.duration_minutes, draft?.meta?.hours, resolvedService?.duration_minutes]);
  // BookingBasket parses the duration string — pass a plain number so
  // its `parseDuration` resolves to the same hours we computed here.
  const basketDurationStr = String(basketHours);
  const basketHasPets =
    typeof draft?.meta?.has_pets === 'string' ? draft.meta.has_pets : 'no';
  const basketAddons = Array.isArray(draft?.meta?.addons)
    ? (draft.meta.addons as { id: string; duration: string }[])
    : [];
  const basketCommercialSpace =
    typeof draft?.meta?.commercial_space === 'string'
      ? draft.meta.commercial_space
      : undefined;
  const basketCommercialOptions = useMemo(() => {
    const cat = categories.find((c) => c.id === draft?.service_category_id);
    return (cat?.flow_config?.commercial_spaces ?? []) as Array<{
      id: string;
      label: string;
    }>;
  }, [categories, draft?.service_category_id]);
  // Hair / Beauty & Wellness run the TREATMENT flow, which has no
  // in-wizard "Want a backup?" step — so we ask it here on the review
  // step. SESSION (cleaning/childcare) already captured it in /booking.
  const isTreatmentFlow = useMemo(
    () =>
      (resolvedService?.category?.booking_flow ??
        categories.find((c) => c.id === draft?.service_category_id)
          ?.booking_flow) === 'TREATMENT' ||
      // Fall back to the entry stamp the pro profile sets, so the backup
      // opt-in below doesn't silently disappear when the catalog is slow or
      // empty and the category can't be resolved yet. (Issue 9)
      draft?.meta?.flow_entry === 'TREATMENT',
    [
      resolvedService?.category?.booking_flow,
      categories,
      draft?.service_category_id,
      draft?.meta?.flow_entry,
    ],
  );
  // The booking's category (with its admin-managed flow_config). The
  // "extra" treatment questions below — pets, expectations, backup — are
  // NOT hardcoded: each shows only when this category's flow_config opts
  // in, so an admin controls them per category (e.g. Hair shows none by
  // default). Mirrors how the SESSION wizard reads flow_config.questions.
  const bookingCategory = useMemo(() => {
    const catId =
      resolvedService?.category?.id ?? draft?.service_category_id ?? null;
    return categories.find((c) => c.id === catId) ?? null;
  }, [categories, resolvedService?.category?.id, draft?.service_category_id]);
  const flowQuestions = bookingCategory?.flow_config?.questions ?? [];
  const petsQuestion = flowQuestions.find((q) => q.type === 'YES_NO');
  const expectationsQuestion = flowQuestions.find((q) => q.type === 'TEXT');
  // Backup opt-in shows for every TREATMENT category by default (Hair,
  // Beauty & Wellness) — reinstated after it regressed. An admin can still
  // hide it for a specific category by setting flow_config.ask_backup=false.
  const asksBackup = bookingCategory?.flow_config?.ask_backup !== false;
  const backupPreference =
    typeof draft?.meta?.substitute_preference === 'string'
      ? draft.meta.substitute_preference
      : '';
  const setBackupPreference = (choice: 'yes' | 'no') =>
    patchBookingDraft({
      meta: { ...(draft?.meta ?? {}), substitute_preference: choice },
    });
  // Pets question — TREATMENT flow only (Hair, Beauty & Wellness). These
  // are in-home services, so the pro needs to know about pets; the SESSION
  // wizard asks this earlier in /booking. We stamp meta.has_pets exactly
  // like the SESSION flow does, so the basket + checkout pick it up.
  const petsAnswer =
    typeof draft?.meta?.has_pets === 'string' ? draft.meta.has_pets : '';
  const setPetsAnswer = (choice: 'yes' | 'no') =>
    patchBookingDraft({
      meta: { ...(draft?.meta ?? {}), has_pets: choice },
    });
  // "Your expectations concerning the pro" + reference photos — TREATMENT
  // flow only (Hair, Beauty & Wellness). The SESSION wizard collects these
  // on /booking step 5; treatment has no such step, so ask here. Stamped
  // straight onto the draft so checkout sends customer_note +
  // customer_note_images exactly like the SESSION flow.
  const expectationsNote =
    typeof draft?.customer_note === 'string' ? draft.customer_note : '';
  const expectationImages = Array.isArray(draft?.customer_note_images)
    ? draft.customer_note_images
    : [];
  const basketOtherCommercial =
    typeof draft?.meta?.commercial_space_other === 'string'
      ? draft.meta.commercial_space_other
      : undefined;
  const basketCategoryName = resolvedService?.category?.name;
  // walletAmountToApply state is owned above (lifted so the basket's
  // amount input can edit it). The basket clamps the value to
  // min(balance, outstanding) on every interaction, and the backend
  // re-caps at the actual outstanding amount as a defence-in-depth.

  // ---------- Handlers ----------
  const handleBack = () => {
    const draftCategory = categories.find(
      (c) => c.id === draft?.service_category_id,
    );
    // Decide SESSION vs TREATMENT robustly so a hair / beauty booking's
    // Back never drops into the cleaning ("choose your session") wizard:
    //   1. The resolved service's own category flow is authoritative when
    //      the catalog has loaded.
    //   2. Otherwise the explicit `flow_entry` stamp the entry points set
    //      (/booking → SESSION, pro profile → TREATMENT) — catalog-
    //      independent and overwritten on each new booking.
    //   3. The draft category is only a last resort.
    // Crucially we only treat it as SESSION when a signal positively says
    // so; anything uncertain falls through to the TREATMENT back-walk,
    // which never lands on the cleaning wizard.
    const serviceFlow = resolvedService?.category?.booking_flow;
    const entryFlow = draft?.meta?.flow_entry as
      | 'SESSION'
      | 'TREATMENT'
      | undefined;
    const isSessionFlow = serviceFlow
      ? serviceFlow === 'SESSION'
      : entryFlow
        ? entryFlow === 'SESSION'
        : draftCategory?.booking_flow === 'SESSION';

    // SESSION (Cleaning, Childcare) collected the date + time back in the
    // /booking wizard and lands here straight on the review step (3). Its
    // own date/time steps (1 = "Pick a date", 2 = "Pick a time") are NEVER
    // part of the cleaning flow, so Back must NOT walk the customer down
    // into them — that's the reported bug where Back from the review went
    // to "Pick a date", then another Back jumped to "Prefer a backup pro".
    // Instead, return straight to /booking (which resumes at the step the
    // customer left — the genuine previous page). Date/time changes are
    // made in the wizard, not here.
    if (isSessionFlow) {
      navigate('/booking');
      return;
    }

    // TREATMENT (Hair, Beauty & Wellness) picks date + time HERE, so Back
    // walks its own schedule steps first (3 → 2 → 1).
    if (step > 1) {
      setStep(step - 1);
      return;
    }
    // Leaving the wizard (step 1). Use an explicit destination rather than
    // navigate(-1): the history stack is unreliable here — a sign-in
    // detour, Stripe redirect, or hard refresh can leave it pointing
    // somewhere irrelevant. The customer configured the booking on the
    // pro's profile, so return there when we have one; otherwise the pro
    // picker. (A SESSION category is already handled above, so /booking is
    // never reached as a fallback for a treatment booking.)
    if (draft?.professional_id) {
      navigate(`/public-profile?id=${draft.professional_id}`);
      return;
    }
    navigate('/select-professional');
  };

  const handleNext = () => {
    if (step === 1) {
      if (slots.length === 0 && !slotsLoading) {
        toast.error('No slots on this date — try another.');
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!selectedSlot) {
        toast.error('Pick a time slot to continue.');
        return;
      }
      setStep(3);
      return;
    }
  };

  const handleBookAndPay = async () => {
    // Hard guard against double-submit. The button is also disabled
    // via `submitting`, but React's render is asynchronous and a
    // double-tap can fire two onClicks before the disabled prop
    // lands. Without this guard the second click created a second
    // booking row + second Stripe session against the same draft
    // (audit 2026-06-04, high).
    if (submitting) return;
    // Surface the reason the button silently no-op'd — previously
    // this returned without any feedback, so a customer hitting
    // "Book and Pay" with a stale draft saw nothing happen at all.
    if (missing.length > 0) {
      toast.error(
        `Your booking still needs ${missingPhrase}. Start your booking from the top.`,
      );
      return;
    }
    if (!selectedSlot) {
      toast.error('Pick a time slot before continuing.');
      // Drop back to the slot picker so the customer can correct it.
      setStep(2);
      return;
    }
    if (!draft?.address?.address_line1) {
      toast.error('Add your service address before paying.');
      return;
    }
    setSubmitting(true);
    // Re-read the wallet balance right before submission. The basket's
    // walletAvailable cap was fetched on mount; if anything spent
    // wallet in another tab between then and the click, our local cap
    // could let the customer request more than they actually have. Clamp
    // walletAmountToApply against the freshest balance so we never send
    // an over-spend to the server.
    let safeWalletAmount = walletAmountToApply;
    try {
      const fresh = await getMyWallet();
      const liveBalance = Number(fresh?.balance ?? 0);
      if (liveBalance !== walletBalance) {
        setWalletBalance(liveBalance);
      }
      if (safeWalletAmount > liveBalance) {
        safeWalletAmount = Math.max(0, liveBalance);
        setWalletAmountToApply(safeWalletAmount);
        if (safeWalletAmount === 0) setUseWallet(false);
        toast.error(
          `Your wallet only has £${liveBalance.toFixed(2)} — we've adjusted the amount.`,
        );
      }
    } catch {
      // Network blip — fall through with the cached value. Backend
      // still caps to the actual balance, so this is safe.
    }
    try {
      // Lock the draft to ONE_OFF before redirecting to Stripe — the
      // post-payment confirmation page reads draft.mode to pick the
      // right confirm endpoint, and a stale 'SUBSCRIPTION' value
      // (left over from a recurring choice earlier in the wizard)
      // routes the customer to "Subscription not found".
      patchBookingDraft({
        mode: 'ONE_OFF',
        wallet_amount: safeWalletAmount,
      });
      const scheduledStart = combineDateAndTime(date, selectedSlot);
      const res = await createOneOffCheckout({
        service_category_id: draft.service_category_id ?? resolvedService?.category?.id ?? '',
        service_id: draft.service_id ?? resolvedService?.id ?? '',
        professional_id: draft.professional_id ?? null,
        scheduled_start: scheduledStart,
        duration_minutes:
          draft.duration_minutes ?? resolvedService?.duration_minutes ?? 60,
        address_line1: draft.address.address_line1 ?? '',
        address_line2: draft.address.address_line2 ?? null,
        city: draft.address.city ?? null,
        postcode: draft.address.postcode ?? null,
        country_code: draft.address.country_code ?? null,
        latitude: draft.address.latitude ?? null,
        longitude: draft.address.longitude ?? null,
        // Use the services the customer picked on the profile page.
        // Fall back to a single line only if the draft has none (direct
        // nav without going through the profile picker).
        items:
          draft.items && draft.items.length > 0
            ? draft.items
            : [
                {
                  service_id: draft.service_id ?? null,
                  quantity: 1,
                  properties: draft.meta ?? null,
                },
              ],
        customer_voucher_id: draft.customer_voucher_id ?? null,
        // Pass the human-typeable voucher code the customer entered
        // into the basket so Stripe actually charges the discounted
        // price. Backend resolves to the customer's CustomerVoucher
        // row server-side.
        voucher_code:
          typeof draft.meta?.coupon_code === 'string'
            ? draft.meta.coupon_code
            : null,
        wallet_amount: safeWalletAmount,
        customer_note: draft.customer_note ?? null,
        customer_note_images: draft.customer_note_images ?? null,
        currency: draft.currency ?? resolvedService?.currency ?? 'GBP',
        // Default to allowing a backup unless the customer explicitly
        // opted out ('no'). Matches the opt-in checkbox, which is ticked
        // by default — an unset preference means "happy for us to assign".
        accept_backup: draft.meta?.substitute_preference !== 'no',
        secondary_professional_ids: Array.isArray(
          draft.meta?.secondary_professional_ids,
        )
          ? (draft.meta?.secondary_professional_ids as string[])
          : [],
        // Echo the basket's pricing_version so the backend can 409
        // PRICING_CHANGED if admin tweaked tax / charges since the
        // last basket /quote.
        pricing_version: pricingVersion ?? undefined,
      });

      if (res.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }
      // Wallet + discount covered the entire bill — backend already
      // flipped the booking to CONFIRMED / PENDING_PROFESSIONAL_CONFIRM
      // inline and there is no Stripe session to confirm. Jump
      // straight to the booking detail page so the customer sees the
      // success state without bouncing through /booking/confirmation
      // (that page tries to confirm against a session_id, which is
      // null on this path and would render an error).
      clearBookingDraft();
      // Branch on the backend-reported booking_status so the toast
      // matches what the row actually shows. "Booking confirmed" on a
      // PENDING_PROFESSIONAL_CONFIRM row was misleading (audit
      // 2026-06-04, medium).
      const isPendingPro =
        res.booking_status === 'PENDING_PROFESSIONAL_CONFIRM';
      toast.success(
        isPendingPro
          ? 'Booking placed — waiting for your professional to accept.'
          : 'Booking confirmed — your wallet covered the total.',
      );
      if (res.id) {
        navigate(`/bookings/${res.id}`);
        return;
      }
      // Fallback if the backend somehow returns no id alongside the
      // 0-card-amount path — list page still lets the customer find it.
      navigate('/bookings');
    } catch (err) {
      // Rate-lock conflict: platform-wide pricing changed since the
      // last basket /quote (audit 2026-06-04). Open a ConfirmModal
      // showing old vs new total + the new pricing_version so the
      // customer can re-confirm before we retry. Detection rules:
      // HTTP 409 OR a backend code === 'PRICING_CHANGED'.
      const status = (err as any)?.response?.status;
      const body = (err as any)?.response?.data;
      const isPricingChanged =
        status === 409 || body?.code === 'PRICING_CHANGED';
      if (isPricingChanged && body?.info) {
        const oldTotal = Number(serverQuoteTotal ?? 0);
        const newTotal = Number(body.info.total_amount ?? oldTotal);
        const newVersion = String(body.info.pricing_version ?? '');
        setPricingVersion(newVersion || null);
        setPendingPriceChange({
          oldTotal,
          newTotal,
          newVersion,
          currency: String(body.info.currency ?? 'GBP'),
        });
        return;
      }
      // The pro's slot filled between basket and pay (also a 409, but with
      // no `info` so it isn't the pricing-changed case). Show a clear,
      // friendly message and point the customer at the schedule step —
      // never the raw "messages.booking.slotTaken" key. (Their own unpaid
      // hold from a prior attempt is excluded server-side, so this only
      // fires for a genuinely-taken slot.)
      if (body?.code === 'SLOT_TAKEN') {
        toast.error(
          'Sorry, that time slot is no longer available. Please go back and pick another time.',
        );
        return;
      }
      toast.error(
        customerApiErrorMessage(err, 'Could not start checkout.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Pre-flight error screen ----------
  if (missing.length > 0) {
    return (
      <div className="min-h-screen bg-[#fcfcfc] flex flex-col">
        <MainHeader />
        <div className="max-w-[640px] mx-auto px-4 md:px-8 py-16 text-center">
          <span className="material-icons text-[#eee] text-[48px] mb-3">
            error_outline
          </span>
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] mb-3">
            Something's missing from your booking
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-6">
            {onlyProMissing ? (
              <>
                We need {missingPhrase} before we can take payment. Choose one
                and we'll pick up right where you left off.
              </>
            ) : (
              <>
                We need {missingPhrase} before we can take payment. Head back to
                the home page to start your booking from the top.
              </>
            )}
          </p>
          <PrimaryButton
            onClick={() =>
              navigate(onlyProMissing ? '/select-professional' : '/')
            }
            className="px-8 h-[48px]"
          >
            {onlyProMissing ? 'Choose a professional' : 'Start a new booking'}
          </PrimaryButton>
        </div>
        <Footer />
      </div>
    );
  }

  const steps = [
    { id: 1, label: 'Location', active: true },
    { id: 2, label: 'Session', active: true },
    { id: 3, label: 'Schedule', active: step >= 1 },
    { id: 4, label: 'Confirm', active: step >= 3 },
  ];

  return (
    <div className="min-h-screen bg-[#fcfcfc] flex flex-col">
      <MainHeader />

      <div className="max-w-[1140px] mx-auto w-full px-4 md:px-8 pt-6">
        <SecondaryButton
          onClick={handleBack}
          icon={
            <span className="material-icons text-[18px]">navigate_before</span>
          }
          className="!bg-[#f7f7f7] !border-none !rounded-[10px] !h-[44px]"
        >
          Back
        </SecondaryButton>
      </div>

      <div className="flex-1 max-w-[1140px] mx-auto px-4 md:px-8 py-8 w-full">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main Content Area */}
          <div className="flex-1">
            <div className="text-center mb-6">
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[25px] md:text-[34px] leading-[32px] md:leading-[48px] mb-10 max-w-[700px] mx-auto">
                {step === 1
                  ? 'Pick a date for your appointment'
                  : step === 2
                    ? `Pick a time on ${format(date, 'EEEE d MMMM')}`
                    : 'Review and confirm'}
              </h1>

              {/* Stepper */}
              <div className="flex items-center justify-center mb-8 w-full px-2">
                {steps.map((s, index) => (
                  <div key={s.id} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-[24px] h-[24px] rounded-full flex items-center justify-center mb-2 ${
                          s.active ? 'bg-[#8e2a2f]' : 'bg-[#edf2f0]'
                        }`}
                      >
                        <div
                          className={`rounded-full ${
                            s.active
                              ? 'w-[8px] h-[8px] bg-white'
                              : 'w-[6px] h-[6px] bg-[#c6d8d3]'
                          }`}
                        />
                      </div>
                      <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] md:text-sm leading-[21px] tracking-[-0.1px] whitespace-nowrap font-bold">
                        {s.label}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`w-12 sm:w-20 md:w-28 h-[2px] mb-[29px] mx-1 shrink-0 ${
                          steps[index + 1].active ? 'bg-[#8e2a2f]' : 'bg-[#edf2f0]'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Step Content */}
            <div className="max-w-[540px] mx-auto w-full">
              {step === 1 ? (
                <div className="max-w-[420px] mx-auto border border-[#ede8e9] rounded-[15px] p-6 shadow-[0px_4px_20px_rgba(0,0,0,0.02)]">
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="w-full h-[54px] rounded-[10px] border border-[#ede8e9] bg-white px-4 flex items-center justify-between font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[15px] hover:border-[#8e2a2f] transition-colors"
                      >
                        <span>{format(date, 'EEEE d MMMM yyyy')}</span>
                        <span className="material-icons text-[#8e2a2f]">
                          calendar_month
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => {
                          if (d) {
                            setDate(d);
                            setSelectedSlot('');
                            setCalendarOpen(false);
                          }
                        }}
                        disabled={(d) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          if (d < today) return true;
                          // Don't flash every day as disabled while the
                          // availability window is still loading.
                          if (slotsLoading) return false;
                          // Enable only days the pro has confirmed open
                          // slots on. Everything else (holidays,
                          // non-working weekdays, fully-booked days,
                          // dates past the fetched horizon) stays
                          // disabled so the customer can't book a date
                          // the pro isn't actually available for.
                          return !availableYmds.has(ymd(d));
                        }}
                      />
                    </PopoverContent>
                  </Popover>

                  <p className="mt-4 font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] tracking-[-0.1px]">
                    {slotsLoading
                      ? 'Checking availability…'
                      : slots.length > 0
                        ? `${slots.length} slot${slots.length === 1 ? '' : 's'} available on this date.`
                        : nextAvailableDate
                          ? `No slots on this date. Next available: ${format(
                              new Date(`${nextAvailableDate}T00:00:00`),
                              'EEE d MMM',
                            )}.`
                          : 'This pro has no upcoming availability.'}
                  </p>
                  {/* "Browse other pros" is intentionally NOT rendered on this
                      date step. It belongs on the professional-selection step
                      (the public profile) — pressing Back from checkout used to
                      surface it here on the "Select a Date" screen, which it
                      shouldn't. (Issue 9) */}
                </div>
              ) : step === 2 ? (
                <div className="w-full">
                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-center text-sm leading-[21px] tracking-[-0.1px] -mt-6 mb-8">
                    Pick the slot that works best for you.
                  </p>
                  {slotsLoading ? (
                    <p className="py-12 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px]">
                      Loading available times…
                    </p>
                  ) : slotsError ? (
                    <p className="py-12 text-center text-[#991b1b] font-['DM_Sans',sans-serif] text-[14px]">
                      {slotsError}
                    </p>
                  ) : slots.length === 0 ? (
                    <div className="py-12 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px] space-y-3">
                      <p>
                        {nextAvailableDate
                          ? `No slots on this date. Next available: ${format(
                              new Date(`${nextAvailableDate}T00:00:00`),
                              'EEE d MMM',
                            )}.`
                          : 'This pro has no upcoming availability.'}
                      </p>
                      {fullyBooked && draft?.service_id ? (
                        <button
                          type="button"
                          onClick={() => navigate('/select-professional')}
                          className="text-[#8e2a2f] text-[13px] font-bold underline"
                        >
                          Browse other pros in this category →
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mb-8">
                      {slots.map((slot) => {
                        // Normalise to "HH:MM" both sides so the click
                        // selection matches what gets written into the
                        // draft (the initial restore from
                        // scheduled_start uses HH:MM too).
                        const slotHm = (slot.start || '').slice(0, 5);
                        const selectedHm = (selectedSlot || '').slice(0, 5);
                        const isSelected = selectedHm === slotHm;
                        return (
                          <button
                            key={slot.start}
                            type="button"
                            onClick={() => setSelectedSlot(slotHm)}
                            className={`py-5 border rounded-lg transition-all font-['DM_Sans',sans-serif] font-bold text-[14px] leading-[24px] ${
                              isSelected
                                ? 'border-[#8e2a2f] shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)] text-[#8e2a2f] bg-white'
                                : 'border-[#eee] text-[#908e8e] bg-white hover:border-[#8e2a2f]'
                            }`}
                          >
                            {slotHm}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full flex flex-col gap-8 items-center">
                  {/* Summary card — what you're about to charge for. */}
                  <div className="w-full max-w-[420px] border border-[#ede8e9] rounded-[15px] p-6 bg-white space-y-3">
                    <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px]">
                      Booking summary
                    </h3>
                    <div className="space-y-2 text-[14px] font-['DM_Sans',sans-serif] text-[#3a3335]">
                      <div className="flex justify-between gap-4">
                        <span className="text-[#908e8e]">Service</span>
                        <span className="font-bold text-right">
                          {basketServiceName}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-[#908e8e]">When</span>
                        <span className="font-bold text-right">
                          {format(date, 'EEEE d MMMM')} at {selectedSlot}
                        </span>
                      </div>
                      {basketAddress ? (
                        <div className="flex justify-between gap-4">
                          <span className="text-[#908e8e]">Where</span>
                          <span className="font-bold text-right">
                            {basketAddress}
                          </span>
                        </div>
                      ) : null}
                      {basketHours > 0 ? (
                        <div className="flex justify-between gap-4">
                          <span className="text-[#908e8e]">Duration</span>
                          <span className="font-bold text-right">
                            {basketHours % 1 === 0
                              ? `${basketHours}h`
                              : `${Math.floor(basketHours)}h ${Math.round((basketHours % 1) * 60)}m`}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Wallet toggle now lives inside the right-side
                      basket — see <BookingBasket walletAvailable=... />
                      below — so all "things that affect the total"
                      cluster together with the coupon code. */}

                  {/* Any pets — shown only when the booking's category
                      opts in via flow_config.questions (a YES_NO question).
                      Admin-managed; hidden by default (e.g. Hair). */}
                  {isTreatmentFlow && petsQuestion ? (
                    <div className="w-full max-w-[420px] mx-auto">
                      <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-bold mb-3 text-center">
                        {petsQuestion.label || 'Do you have any pets at the property?'}
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {(['yes', 'no'] as const).map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setPetsAnswer(opt)}
                            className={`w-full py-4 px-5 rounded-lg border-2 text-center transition-all ${
                              petsAnswer === opt
                                ? 'border-[#8e2a2f] bg-[#8e2a2f]/5'
                                : 'border-[#eee] hover:border-[#8e2a2f]/40'
                            }`}
                          >
                            <span
                              className={`font-['DM_Sans',sans-serif] font-bold text-sm leading-[21px] ${
                                petsAnswer === opt
                                  ? 'text-[#8e2a2f]'
                                  : 'text-[#3a3335]'
                              }`}
                            >
                              {opt === 'yes' ? 'Yes' : 'No'}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Your expectations + reference photos — shown only when
                      the category opts in via flow_config.questions (a TEXT
                      question). Admin-managed; hidden by default. */}
                  {isTreatmentFlow && expectationsQuestion ? (
                    <div className="w-full max-w-[420px] mx-auto">
                      <div className="border border-[#eee] rounded-lg p-5 bg-white focus-within:border-[#8e2a2f] transition-all">
                        <h3 className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-base leading-[24px] mb-2">
                          {expectationsQuestion.label ||
                            'Your expectations concerning the pro'}
                        </h3>
                        <textarea
                          value={expectationsNote}
                          onChange={(e) =>
                            patchBookingDraft({
                              customer_note: e.target.value || null,
                            })
                          }
                          placeholder="Write something..."
                          className="w-full h-[100px] resize-none outline-none font-['DM_Sans',sans-serif] text-sm leading-[21px] text-[#3a3335] placeholder:text-[#908e8e]"
                        />
                      </div>
                      <ExpectationPhotos
                        value={expectationImages}
                        onChange={(urls) =>
                          patchBookingDraft({
                            customer_note_images: urls.length ? urls : null,
                          })
                        }
                      />
                    </div>
                  ) : null}

                  {/* Backup professional opt-in (TREATMENT flow — Hair,
                      Beauty & Wellness). Mirrors the SESSION wizard: tick =
                      happy for us to assign a suitable alternative; untick to
                      browse and pick your own preferred backups (as many as
                      you like). */}
                  {isTreatmentFlow && asksBackup ? (
                    <div className="w-full max-w-[420px] mx-auto">
                      <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-bold mb-3 text-center">
                        Prefer a backup pro?
                      </p>
                      <label className="flex items-start gap-3 p-5 rounded-lg border-2 border-[#ede8e9] bg-white cursor-pointer hover:border-[#8e2a2f] transition-all">
                        <input
                          type="checkbox"
                          checked={backupPreference !== 'no'}
                          onChange={(e) =>
                            setBackupPreference(e.target.checked ? 'yes' : 'no')
                          }
                          className="mt-0.5 w-5 h-5 accent-[#8e2a2f] cursor-pointer shrink-0"
                        />
                        <span className="font-['DM_Sans',sans-serif] text-[14px] leading-[21px] text-[#3a3335]">
                          <span className="font-bold">
                            I&apos;m happy for Cherry Picked to assign a suitable
                            alternative professional
                          </span>{' '}
                          if my chosen pro becomes unavailable. Leave this
                          unticked if you&apos;d rather choose your own preferred
                          alternatives.
                        </span>
                      </label>

                      {backupPreference === 'no' ? (
                        <div className="mt-4 rounded-lg bg-[#f7f2e8] p-5">
                          <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] leading-[21px] mb-3">
                            No problem — you can browse available professionals
                            and tick as many preferred alternatives as you like.
                            We&apos;ll only use them if your chosen pro can&apos;t
                            make it.
                          </p>
                          <button
                            type="button"
                            onClick={() => navigate('/secondary-preferred')}
                            className="inline-flex items-center gap-1 font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#8e2a2f] underline underline-offset-4 hover:text-[#7a2228]"
                          >
                            Browse &amp; choose your preferred alternatives →
                          </button>
                          <p className="mt-3 font-['DM_Sans',sans-serif] text-[#3a3335] text-[12px] leading-[18px]">
                            If your chosen professional becomes unavailable and
                            you have not selected a backup or any preferred
                            alternatives, your booking will be cancelled and
                            refunded in full.
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="text-center max-w-[420px]">
                    <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[12px] font-bold mb-1">
                      Free cancellation per our{' '}
                      <button
                        type="button"
                        onClick={() => setPolicyOpen(true)}
                        className="underline hover:text-[#8e2a2f] cursor-pointer text-inherit font-inherit"
                      >
                        cancellation policy
                      </button>
                      .
                    </p>
                    <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px]">
                      By tapping the button below you accept our{' '}
                      <button
                        type="button"
                        onClick={() => setTermsOpen(true)}
                        className="text-[#8e2a2f] underline cursor-pointer font-inherit"
                      >
                        Terms of service
                      </button>
                      . Payment is handled securely by Stripe.
                    </p>
                  </div>
                </div>
              )}

              {/* Patch-test confirmation — mandatory before payment when the
                  booked treatment requires a patch test. */}
              {step === 3 && needsPatchTest ? (
                <div className="mt-6 max-w-[480px] mx-auto">
                  <label className="flex items-start gap-3 p-4 rounded-lg border-2 border-[#f3d6a0] bg-[#fff7e6] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={patchTestAck}
                      onChange={(e) => setPatchTestAck(e.target.checked)}
                      className="mt-0.5 w-5 h-5 accent-[#8e2a2f] cursor-pointer shrink-0"
                    />
                    <span className="font-['DM_Sans',sans-serif] text-[13px] leading-[20px] text-[#3a3335]">
                      This treatment requires a patch test at least 48 hours
                      before your appointment. If you haven&apos;t had a patch
                      test with this professional before, we recommend booking
                      at least 4 days in advance to allow enough time for your
                      professional to arrange this with you. Please note that if
                      a patch test hasn&apos;t been completed at least 48 hours
                      before your appointment, the treatment won&apos;t be able
                      to go ahead. By booking, you confirm you understand this
                      requirement.
                    </span>
                  </label>
                </div>
              ) : null}

              {/* Action button */}
              <div className="mt-8 max-w-[400px] mx-auto">
                {step < 3 ? (
                  <PrimaryButton
                    onClick={handleNext}
                    className="w-full !h-[54px] !rounded-[10px]"
                    disabled={
                      slotsLoading ||
                      (step === 1 && slots.length === 0) ||
                      (step === 2 && !selectedSlot)
                    }
                  >
                    Next
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    onClick={handleBookAndPay}
                    className="w-full !h-[54px] !rounded-[10px]"
                    disabled={
                      submitting ||
                      catalogLoading ||
                      (needsPatchTest && !patchTestAck)
                    }
                  >
                    {submitting ? 'Redirecting to payment…' : 'Book and Pay'}
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar - Basket (same component as /booking so the
              total is always consistent) */}
          <BookingBasket
            price={basketServicePrice}
            duration={basketDurationStr}
            hasPets={basketHasPets}
            // Only flows that asked the pets question stamp meta.has_pets
            // (cleaning). Childcare etc. never do, so hide the pets line.
            showPets={typeof draft?.meta?.has_pets === 'string'}
            appointmentDate={date}
            appointmentTime={selectedSlot}
            selectedTimes={selectedSlot ? [selectedSlot] : []}
            // "Other times you've kept open" is cleaning-only — hide it for
            // hair, beauty & wellness, and childcare.
            isCleaning={
              String(
                resolvedService?.category?.code ??
                  draft?.meta?.category_code ??
                  '',
              ).toUpperCase() === 'CLEANING'
            }
            onSubscribeClick={handleSubscribeClick}
            // Final-step value so the basket renders the full breakdown
            // (duration line, pets line, add-ons, total).
            sessionStep={12}
            selectedAddons={basketAddons}
            addonsCatalog={addonsCatalog}
            serviceName={basketServiceName}
            categoryName={basketCategoryName}
            commercialSpaceType={basketCommercialSpace}
            commercialSpaceOptions={basketCommercialOptions}
            otherCommercialSpace={basketOtherCommercial}
            pricingUnit={resolvedService?.pricing_unit ?? 'PER_HOUR'}
            walletAmount={walletAmountToApply}
            walletCurrency={walletCurrency}
            walletAvailable={walletBalance}
            walletEnabled={useWallet}
            onToggleWallet={(enabled) => {
              setUseWallet(enabled);
              // Toggle-off zeroes the amount immediately so the next
              // quote round-trip charges the full card. Toggle-on
              // leaves the amount alone — the basket's WalletInput
              // pushes a default (clamped to outstanding) right after.
              if (!enabled) setWalletAmountToApply(0);
            }}
            onWalletAmountChange={(n) => {
              setWalletAmountToApply(n);
              // Flip the enabled flag implicitly: typing > 0 turns
              // wallet on, zero-ing it out turns it off. Keeps the
              // checkbox in sync with the field.
              setUseWallet(n > 0);
            }}
            quoteItems={draft?.items?.length ? draft.items : undefined}
            onQuoteChange={(q) => {
              setPricingVersion(q?.pricing_version ?? null);
              setServerQuoteTotal(
                q?.total_amount != null ? Number(q.total_amount) : null,
              );
            }}
          />
        </div>
      </div>
      <SubscriptionPlanOffcanvas
        isOpen={isSubscriptionPlanOpen}
        onClose={() => setSubscriptionPlanOpen(false)}
        onPickPlan={handlePickPlan}
      />

      <CmsModal
        isOpen={policyOpen}
        onClose={() => setPolicyOpen(false)}
        code="CANCELLATION_POLICY"
        userType="CUSTOMER"
        fallbackTitle="Cancellation Policy"
      />
      <CmsModal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
        code="TERMS_CONDITIONS"
        userType="CUSTOMER"
        fallbackTitle="Terms of Service"
      />

      {/* Rate-lock interstitial — fires when the backend 409s with
          PRICING_CHANGED on Book-and-Pay. Shows the old vs new total
          so the customer can re-confirm before we retry Stripe. */}
      <ConfirmModal
        isOpen={pendingPriceChange !== null}
        onClose={() => {
          if (!submitting) setPendingPriceChange(null);
        }}
        confirming={submitting}
        title="Pricing has been updated"
        message={
          pendingPriceChange
            ? `The total has changed from ${formatMoney(
                pendingPriceChange.oldTotal,
                pendingPriceChange.currency,
              )} to ${formatMoney(
                pendingPriceChange.newTotal,
                pendingPriceChange.currency,
              )}. Please confirm the new total to continue to payment.`
            : ''
        }
        confirmLabel="Confirm and continue"
        cancelLabel="Back to basket"
        onConfirm={() => {
          // Adopt the new version, close the modal, and retry the
          // checkout. The basket will already have re-rendered with
          // the new numbers via the next /quote auto-refresh.
          setPendingPriceChange(null);
          void handleBookAndPay();
        }}
      />

      <Footer />
    </div>
  );
}
